import { skipToken } from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import Icon from '../../components/icon'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { TextInput } from '../../components/inputs/textInput'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import { TabNav } from '../../components/nav/tabNav'
import { TableComponent } from '../../components/table'
import { MiniHeader } from '../../components/typography/miniHeader'
import { PageHeader } from '../../components/typography/PageHeader'
import { PRIMARY_COLOR } from '../../constants'
import { formatCurrency, prettyFormatNumber } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  PLATFORM_COST_BUCKET,
  PLATFORM_COST_BUCKET_LABEL,
  PLATFORM_COST_DEFAULT_PERIOD,
  PLATFORM_COST_MESSAGE,
  PLATFORM_COST_METRIC_KEYS,
  PLATFORM_COST_METRIC_LABEL,
  PLATFORM_COST_REPORT_TYPE,
} from '../../redux-toolkits/platform-cost-metrics/platform-cost-metrics.constant'
import {
  buildPlatformCostMetricsQueryArgs,
  formatPlatformCostBucketLabel,
  mapBucketsToChartSeries,
} from '../../redux-toolkits/platform-cost-metrics/platform-cost-metrics.helpers'
import { useGetPlatformCostMetricsQuery } from '../../redux-toolkits/platform-cost-metrics/platform-cost-metrics.slice'
import type {
  PlatformCostBucketSize,
  PlatformCostMetricKey,
} from '../../redux-toolkits/platform-cost-metrics/platform-cost-metrics.type'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'

const PLATFORM_COST_CHART_GRADIENT_ID = 'platform-cost-chart-gradient'
const PLATFORM_COST_CHART_GRID_COLOR = '#EAEAEA'
const PLATFORM_COST_CHART_TICK_COLOR = '#555555'
const PLATFORM_COST_CURRENCY_SYMBOL = '₦'

const BUCKET_TABS: Array<{ name: string; id: PlatformCostBucketSize }> = [
  {
    name: PLATFORM_COST_BUCKET_LABEL[PLATFORM_COST_BUCKET.DAY],
    id: PLATFORM_COST_BUCKET.DAY,
  },
  {
    name: PLATFORM_COST_BUCKET_LABEL[PLATFORM_COST_BUCKET.WEEK],
    id: PLATFORM_COST_BUCKET.WEEK,
  },
  {
    name: PLATFORM_COST_BUCKET_LABEL[PLATFORM_COST_BUCKET.MONTH],
    id: PLATFORM_COST_BUCKET.MONTH,
  },
]

const isPlatformCostBucketSize = (
  value: string | null,
): value is PlatformCostBucketSize =>
  value === PLATFORM_COST_BUCKET.DAY ||
  value === PLATFORM_COST_BUCKET.WEEK ||
  value === PLATFORM_COST_BUCKET.MONTH

const isPlatformCostMetricKey = (
  value: string,
): value is PlatformCostMetricKey =>
  (PLATFORM_COST_METRIC_KEYS as readonly string[]).includes(value)

/**
 * Finance module page for Platform Cost Metrics: date/bucket controls, totals,
 * chart/table, and Excel email/download via the shared export modal.
 */
export const PlatformCostMetricsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const dateFilter = useMemo(() => {
    const customDateRange = searchParams.get('isCustomDateRange') === 'true'
    return {
      isCustomDateRange: customDateRange,
      startDate: customDateRange
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: customDateRange
        ? searchParams.get('endDate') || undefined
        : undefined,
      filter: customDateRange
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) ||
          PLATFORM_COST_DEFAULT_PERIOD,
    }
  }, [searchParams])

  const bucketParam = searchParams.get('bucket')
  const bucket = isPlatformCostBucketSize(bucketParam)
    ? bucketParam
    : PLATFORM_COST_BUCKET.DAY

  const metricParam = searchParams.get('metric')
  const selectedMetricKey: PlatformCostMetricKey | null =
    metricParam !== null && isPlatformCostMetricKey(metricParam)
      ? metricParam
      : null

  const metricsQueryArgs = useMemo(() => {
    return buildPlatformCostMetricsQueryArgs({
      ...dateFilter,
      bucket,
    })
  }, [bucket, dateFilter])

  const {
    data: metrics,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPlatformCostMetricsQuery(metricsQueryArgs ?? skipToken, {
    refetchOnMountOrArgChange: true,
  })

  const chartSeries = useMemo(() => {
    if (metrics === undefined || selectedMetricKey === null) {
      return { labels: [] as string[], data: [] as number[] }
    }
    return mapBucketsToChartSeries({
      buckets: metrics.buckets,
      metricKey: selectedMetricKey,
      bucket,
    })
  }, [bucket, metrics, selectedMetricKey])

  const chartData = useMemo(
    () =>
      chartSeries.labels.map((label, index) => ({
        label,
        value: chartSeries.data[index] ?? 0,
      })),
    [chartSeries],
  )

  const loadErrorMessage = (() => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'error' in error &&
      typeof error.error === 'string' &&
      error.error !== ''
    ) {
      return error.error
    }
    return PLATFORM_COST_MESSAGE.LOAD_ERROR
  })()

  return (
    <Layout>
      <div className="flex flex-col md:items-center md:flex-row md:justify-between gap-4 mb-10">
        <PageHeader
          title="Platform Cost Metrics"
          className="capitalize !mb-0"
        />

        <div className="flex flex-col md:flex-row space-y-8 md:space-y-0">
          <DateFilterNav
            filter={dateFilter?.filter ?? 'custom'}
            isCustomDateRange={dateFilter?.isCustomDateRange ?? false}
            startDate={dateFilter?.startDate}
            endDate={dateFilter?.endDate}
            setFilter={(filter) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                filter: filter.filter ?? '',
                isCustomDateRange: String(filter.isCustomDateRange),
                endDate: filter.endDate ?? '',
                startDate: filter.startDate ?? '',
              })
            }}
          />

          <Button
            type="button"
            label="Export"
            primary={true}
            onClick={() => {
              handleModalOpen('exportDataModal')
            }}
            className="!w-full md:!max-w-[131px] md:ml-10"
          />
        </div>
      </div>

      {metricsQueryArgs === null ? (
        <div className="rounded-2xl border border-divider-gray p-8 text-center text-sec-black">
          {PLATFORM_COST_MESSAGE.UNRESOLVED_DATE_RANGE}
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-divider-gray p-8 text-center">
          <p className="text-red-700">{loadErrorMessage}</p>
          <Button
            type="button"
            label="Try again"
            primary={false}
            onClick={() => {
              refetch()
            }}
            className="!mx-auto !mt-4 !w-auto"
          />
        </div>
      ) : (
        <>
          <section className="mb-10" aria-label="Cost overview">
            <div className="mb-4">
              <MiniHeader name="Cost overview" />
              <p className="mt-1 text-sm text-sec-black">
                Select a cost to explore its trend.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {PLATFORM_COST_METRIC_KEYS.map((metricKey) => (
                <button
                  key={metricKey}
                  type="button"
                  aria-pressed={selectedMetricKey === metricKey}
                  aria-label={`Show ${PLATFORM_COST_METRIC_LABEL[metricKey]} on chart`}
                  className={classNames(
                    'rounded-2xl text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    {
                      'ring-2 ring-primary ring-offset-2':
                        selectedMetricKey === metricKey,
                      'hover:-translate-y-0.5 hover:shadow-3xl':
                        selectedMetricKey !== metricKey,
                    },
                  )}
                  onClick={() => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      metric: metricKey,
                    })
                  }}
                >
                  <CountCard
                    count={metrics?.totals[metricKey]}
                    text={PLATFORM_COST_METRIC_LABEL[metricKey]}
                    isCurrency={true}
                    loading={isLoading}
                  />
                </button>
              ))}
            </div>
          </section>

          <section
            className="mb-10 overflow-hidden rounded-2xl border border-divider-gray bg-white"
            aria-labelledby="cost-trend-heading"
          >
            <div className="flex flex-col gap-5 border-b border-divider-gray p-5 md:flex-row md:items-start md:justify-between md:p-6">
              <div>
                <p className="mb-1 text-sm text-sec-black">Cost trend</p>
                <h2 id="cost-trend-heading" className="text-lg font-semibold">
                  {selectedMetricKey === null
                    ? PLATFORM_COST_MESSAGE.SELECT_METRIC_TITLE
                    : PLATFORM_COST_METRIC_LABEL[selectedMetricKey]}
                </h2>
                {selectedMetricKey !== null ? (
                  <p className="mt-2 text-2xl font-bold text-black">
                    {formatCurrency(metrics?.totals[selectedMetricKey] ?? 0)}
                  </p>
                ) : undefined}
                {metricsQueryArgs !== null ? (
                  <p className="mt-1 text-xs text-sec-black">
                    {metricsQueryArgs.startDate} to {metricsQueryArgs.endDate}
                  </p>
                ) : undefined}
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-sec-black">
                  Group data by
                </span>
                <TabNav
                  activeTab={bucket}
                  setActiveTab={(selected) => {
                    if (!isPlatformCostBucketSize(selected)) return
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      bucket: selected,
                    })
                  }}
                  tabs={BUCKET_TABS}
                  type="switcher-outline"
                  size="small"
                  className="w-max max-w-full"
                />
              </div>
            </div>

            <div className="relative min-h-[360px] p-4 md:p-6">
              {isFetching && !isLoading && selectedMetricKey !== null ? (
                <p
                  className="absolute right-6 top-3 text-xs font-medium text-primary"
                  role="status"
                >
                  Updating…
                </p>
              ) : undefined}

              {selectedMetricKey === null ? (
                <div className="flex h-[320px] items-center justify-center rounded-xl bg-light-gray-bg px-6 text-center text-sm text-sec-black">
                  {PLATFORM_COST_MESSAGE.SELECT_METRIC}
                </div>
              ) : isLoading ? (
                <div
                  className="h-[320px] animate-pulse rounded-xl bg-light-gray-bg"
                  aria-label="Loading cost trend"
                  role="status"
                />
              ) : chartSeries.data.length === 0 ? (
                <div className="flex h-[320px] items-center justify-center rounded-xl bg-light-gray-bg px-6 text-center text-sm text-sec-black">
                  No cost activity was recorded for this range.
                </div>
              ) : (
                <div
                  className="h-[320px] w-full"
                  role="group"
                  aria-label={`${PLATFORM_COST_METRIC_LABEL[selectedMetricKey]} cost trend`}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                      accessibilityLayer
                    >
                      <defs>
                        <linearGradient
                          id={PLATFORM_COST_CHART_GRADIENT_ID}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={PRIMARY_COLOR}
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="100%"
                            stopColor={PRIMARY_COLOR}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        stroke={PLATFORM_COST_CHART_GRID_COLOR}
                        strokeDasharray="4 4"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        minTickGap={24}
                        tick={{
                          fill: PLATFORM_COST_CHART_TICK_COLOR,
                          fontSize: 12,
                        }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        width={64}
                        tick={{
                          fill: PLATFORM_COST_CHART_TICK_COLOR,
                          fontSize: 12,
                        }}
                        tickFormatter={(value: number) =>
                          `${PLATFORM_COST_CURRENCY_SYMBOL}${prettyFormatNumber(
                            value,
                          )}`
                        }
                      />
                      <Tooltip
                        cursor={{
                          stroke: PRIMARY_COLOR,
                          strokeWidth: 1,
                          strokeDasharray: '6 6',
                        }}
                        content={({ active, payload, label }) => {
                          if (!active || payload?.[0] === undefined) return null
                          return (
                            <div className="rounded-lg border border-divider-gray bg-white px-3 py-2 shadow-dropdown-custom">
                              <p className="text-xs text-sec-black">{label}</p>
                              <p className="mt-1 text-sm font-semibold text-black">
                                {formatCurrency(Number(payload[0].value ?? 0))}
                              </p>
                            </div>
                          )
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={PRIMARY_COLOR}
                        strokeWidth={2.5}
                        fill={`url(#${PLATFORM_COST_CHART_GRADIENT_ID})`}
                        activeDot={{ r: 5, strokeWidth: 2, fill: '#ffffff' }}
                        dot={{ r: 3, strokeWidth: 2, fill: '#ffffff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>

          <section aria-label="Detailed cost breakdown">
            <div className="mb-4">
              <MiniHeader name="Detailed breakdown" />
              <p className="mt-1 text-sm text-sec-black">
                Review the values behind each point in the trend.
              </p>
            </div>
            <TableComponent
              name="platform-cost-buckets"
              headers={[
                'Period',
                ...PLATFORM_COST_METRIC_KEYS.map(
                  (metricKey) => PLATFORM_COST_METRIC_LABEL[metricKey],
                ),
              ]}
              rows={(metrics?.buckets ?? []).map((costBucket) => ({
                id: `${costBucket.periodStart}-${costBucket.periodEnd}`,
                content: [
                  formatPlatformCostBucketLabel({
                    periodStart: costBucket.periodStart,
                    periodEnd: costBucket.periodEnd,
                    bucket,
                  }),
                  ...PLATFORM_COST_METRIC_KEYS.map((metricKey) =>
                    formatCurrency(costBucket[metricKey]),
                  ),
                ],
              }))}
              loading={isFetching}
              isEmpty={!isFetching && (metrics?.buckets.length ?? 0) === 0}
              emptyText="No platform cost buckets for this range."
            />
          </section>
        </>
      )}

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{
          bucket,
        }}
        title="Export Platform Cost Metrics"
        reportType={PLATFORM_COST_REPORT_TYPE}
      >
        {(form) => (
          <Dropdown
            menuClassName=""
            menuButton={
              <TextInput
                type="text"
                name="bucket"
                placeholder="Bucket"
                title="Bucket"
                value={
                  PLATFORM_COST_BUCKET_LABEL[
                    (form.values.bucket as PlatformCostBucketSize) ??
                      PLATFORM_COST_BUCKET.DAY
                  ]
                }
                onBlur={form.handleBlur}
                onChange={form.handleChange}
                errors=""
                hasIcon={false}
                extra={
                  <Icon
                    id="left-caret"
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
              />
            }
            menuItems={BUCKET_TABS.map((bucketOption) => ({
              name: bucketOption.name,
              value: bucketOption.id,
              onClick: () => {
                form.setFieldValue('bucket', bucketOption.id)
              },
            }))}
          />
        )}
      </ExportDataModal>
    </Layout>
  )
}

export default PlatformCostMetricsPage
